// src/componants/common/Main.js
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import FeatureGate from "./FeatureGate";
import { getMyEmployee } from "../../api";
import { createAppeal } from "../../api/appeals";
import { mapEmployeeRow } from "../../utils/dataMappers";
import { getRequestSubmitHint } from "../../utils/requestHelpers";
import {
  buildEmployeeHomeStats,
  buildDepartmentManagerHomeStats,
  buildManagerHomeStats,
  buildHomeMyRequests,
  buildUpcomingApprovedLeaves,
  filterMyPenalties,
  resolveMyEmployeeRecord,
} from "../../utils/homeStatsHelpers";
import MyPenaltiesSections from "./MyPenaltiesSections";
import HomeLeaveRequests from "./HomeLeaveRequests";
import HomeUpcomingShifts from "./HomeUpcomingShifts";
import HomeRecentRequests from "./HomeRecentRequests";
import { useToast } from "../../context/ToastContext";
import HomeQuickActions from "./HomeQuickActions";
import { useNavigation } from "../../context/NavigationContext";
import { getResubmitNavigation } from "../../utils/requestTransparency";
import "./Main.css";
import MyRequestsHub from "../employee/MyRequestsHub";
import AdvanceRequestForm from "../employee/AdvanceRequestForm";
import RemoteAttendance from "../employee/RemoteAttendance";
import HomeAttendanceClock from "../employee/HomeAttendanceClock";
import ManagerTeamAttendance from "./ManagerTeamAttendance";
import WfhRequestForm from "../employee/WfhRequestForm";
import OvertimeRequestForm from "../employee/OvertimeRequestForm";
import MyComplaints from "../employee/MyComplaints";
import MyDaysView from "../employee/MyDaysView";
import ReceivedComplaints from "../manager/ReceivedComplaints";
import InvestigationsPage from "../investigations/InvestigationsPage";
import ResignationReview from "./ResignationReview";
import PendingApprovals from "../manager/PendingApprovals";
import TeamManagement from "../manager/TeamManagement";
import EmployeeAgenda from "../manager/EmployeeAgenda";
import TeamLeaveOvertimeView from "../manager/TeamLeaveOvertimeView";
import PenaltyManager from "../manager/PenaltyManager";
import BranchesDepartments from "../department-manager/BranchesDepartments";
import AppealsManager from "../manager/AppealsManager";
import RequestsNotifications from "../employee/RequestsNotifications";
import MyAppeals from "../employee/MyAppeals";
import HrDashboard from "../hr/HrDashboard";
import HrEmployeeManagement from "../hr/HrEmployeeManagement";
import HrWorkModeManagement from "../hr/HrWorkModeManagement";
import HrAdvanceApprovals from "../hr/HrAdvanceApprovals";
import HrOrgStructure from "../hr/HrOrgStructure";
import HrShortageManager from "../hr/HrShortageManager";
import HrEvaluations from "../hr/HrEvaluations";
import EvaluationResultsBoard from "../hr/EvaluationResultsBoard";
import HrRecruitment from "../hr/HrRecruitment";
import HrAnnouncements from "../hr/HrAnnouncements";
import HrCompanyPolicies from "../hr/HrCompanyPolicies";
import HrLeaveGrant from "../hr/HrLeaveGrant";
import HrOvertimeGrant from "../hr/HrOvertimeGrant";
import HrWorkflows from "../hr/HrWorkflows";
import AnnouncementsInbox from "./AnnouncementsInbox";
import CompanyPoliciesInbox from "./CompanyPoliciesInbox";
import MyEvaluations from "../employee/MyEvaluations";
import InternalJobs from "../employee/InternalJobs";
import ManagerRecruitment from "../manager/ManagerRecruitment";
import AdvanceDisbursementRouting from "../advance/AdvanceDisbursementRouting";
import AdvanceDisbursementConfirm from "../advance/AdvanceDisbursementConfirm";
import AdvanceDisbursementLedger from "../advance/AdvanceDisbursementLedger";
import HrCashEntitlementCreate from "../hr/HrCashEntitlementCreate";
import CashEntitlementRouting from "../cash-entitlement/CashEntitlementRouting";
import CashEntitlementConfirm from "../cash-entitlement/CashEntitlementConfirm";
import CashEntitlementLedger from "../cash-entitlement/CashEntitlementLedger";
import GmDashboard from "../gm/GmDashboard";
import AdminAccountsManagement from "../admin/AdminAccountsManagement";
import BrandsManagement from "../admin/BrandsManagement";
import CompanyModulesPanel from "../admin/CompanyModulesPanel";
import { isBrandAdminUiEnabled } from "../../config/brandAdminAccess";
import BranchTimesheet from "../../pages/attendance/BranchTimesheet";
import InventoryNightPage from "../../pages/attendance/InventoryNight";
import BranchShiftHours from "../../pages/attendance/BranchShiftHours";
import CustomSchedule from "../../pages/attendance/CustomSchedule";
import TransportLines from "../../pages/attendance/TransportLines";
import AttendanceRegulations from "../../pages/attendance/AttendanceRegulations";
import FlexibleManualAttendance from "../../pages/attendance/FlexibleManualAttendance";
import MyProfile from "../employee/MyProfile";
import MyPayslip from "../../pages/payroll/MyPayslip";
import HrProfileCompletion from "../hr/HrProfileCompletion";
import HrSocialInsurance from "../hr/HrSocialInsurance";
import { isHrFamilyRole, isOwnerRole, canManageApprovalCycles } from "../../utils/hrStaffHelpers";

const KmtUploadPage = lazy(() => import("../../pages/attendance/KmtUpload"));
const KmtPayrollPage = lazy(() => import("../../pages/payroll/KmtPayroll"));
const SalesTargetsPage = lazy(() => import("../../pages/payroll/SalesTargets"));
const ReportsHubPage = lazy(() => import("../reports/ReportsHub"));

function PageFallback() {
  return <div className="ess-placeholder-box">جارٍ فتح الصفحة...</div>;
}

function KmtUpload(props) {
  return (
    <Suspense fallback={<PageFallback />}>
      <KmtUploadPage {...props} />
    </Suspense>
  );
}

function KmtPayroll(props) {
  return (
    <Suspense fallback={<PageFallback />}>
      <KmtPayrollPage {...props} />
    </Suspense>
  );
}

function SalesTargets() {
  return (
    <Suspense fallback={<PageFallback />}>
      <SalesTargetsPage />
    </Suspense>
  );
}

function ReportsHub(props) {
  return (
    <Suspense fallback={<PageFallback />}>
      <ReportsHubPage {...props} />
    </Suspense>
  );
}

const pageInfo = {
  complaints: {
    title: "الشكاوى والاستفسارات",
    subtitle: "تقديم شكوى أو استفسار جديد",
  },
};

function AppealModal({
  isOpen,
  onClose,
  title,
  subtitle,
  onSubmit,
  submitted,
}) {
  const [reason, setReason] = useState("");

  if (!isOpen) return null;

  return (
    <div className="ess-modal-overlay" onClick={onClose}>
      <div className="ess-modal" onClick={(e) => e.stopPropagation()}>
        {!submitted ? (
          <>
            <h3 className="ess-modal-title">{title}</h3>
            <p className="ess-modal-subtitle">{subtitle}</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!reason.trim()) return;
                onSubmit(reason);
                setReason("");
              }}
            >
              <div className="ess-field">
                <label className="ess-label">سبب التظلم</label>
                <textarea
                  className="ess-textarea"
                  rows={4}
                  placeholder="اكتب سبب تظلمك بالتفصيل..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                />
              </div>
              <div className="ess-modal-buttons">
                <button
                  type="button"
                  className="ess-btn-secondary"
                  onClick={onClose}
                >
                  إلغاء
                </button>
                <button type="submit" className="ess-btn-primary">
                  إرسال التظلم
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="ess-success-box">
            <p className="ess-success-title">✅ تم إرسال التظلم بنجاح</p>
            <p className="ess-success-subtitle">
              تم إرسال تظلمك إلى مدير المدير المباشر، وسيتم إشعارك بالرد قريبًا.
            </p>
            <button className="ess-btn-secondary" onClick={onClose}>
              إغلاق
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function useRequestResubmit() {
  const navigation = useNavigation();
  return (request) => {
    const target = getResubmitNavigation(request?.source || request);
    if (!target || !navigation?.navigate) return;
    navigation.navigate(target.id, target.options);
  };
}

function HomeContent() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const handleResubmit = useRequestResubmit();
  const {
    employeePenalties,
    refreshData,
    pendingRequests,
    recentActivities,
    getAllEmployees,
  } = useData();

  const myEmployee = useMemo(
    () => resolveMyEmployeeRecord(getAllEmployees(), user),
    [getAllEmployees, user],
  );

  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    if (myEmployee) {
      setMyProfile(myEmployee);
    }

    let cancelled = false;
    getMyEmployee()
      .then((data) => {
        if (!cancelled && data) {
          setMyProfile(mapEmployeeRow(data));
        } else if (!cancelled && !myEmployee) {
          setMyProfile(null);
        }
      })
      .catch(() => {
        if (!cancelled && !myEmployee) {
          setMyProfile(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [myEmployee, user?.employeeId]);

  const myProfileId = myProfile?.id || user?.employeeId;

  const myPenalties = useMemo(
    () => filterMyPenalties(employeePenalties, myProfileId),
    [employeePenalties, myProfileId],
  );

  const stats = useMemo(
    () =>
      buildEmployeeHomeStats({
        employee: myProfile,
        pendingRequests,
        recentActivities,
        user,
      }),
    [myProfile, pendingRequests, recentActivities, user],
  );

  const requests = useMemo(
    () => buildHomeMyRequests(pendingRequests, user),
    [pendingRequests, user],
  );

  const leaveRequests = useMemo(
    () => buildUpcomingApprovedLeaves(pendingRequests, user),
    [pendingRequests, user],
  );

  const [appealModal, setAppealModal] = useState({
    open: false,
    type: "",
    itemId: null,
    submitted: false,
  });

  function openAppealModal(type, itemId) {
    setAppealModal({ open: true, type, itemId, submitted: false });
  }

  function closeAppealModal() {
    setAppealModal({ open: false, type: "", itemId: null, submitted: false });
  }

  // ✅ التظلم دايمًا يبدأ من المدير المباشر أولًا — التصعيد يحصل بعدين لما المدير يرد
  async function handleAppealSubmit(reason) {
    try {
      await createAppeal({
        type: appealModal.type === "penalty" ? "penalty" : "request",
        reason,
        penaltyId:
          appealModal.type === "penalty" ? appealModal.itemId : undefined,
        relatedId:
          appealModal.type === "request" ? appealModal.itemId : undefined,
      });
      await refreshData();
      setAppealModal((prev) => ({ ...prev, submitted: true }));
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إرسال التظلم";
      showToast(Array.isArray(message) ? message.join("\n") : message, "error");
    }
  }

  const modalTitle =
    appealModal.type === "penalty"
      ? "تقديم تظلم على الجزاء"
      : "تقديم تظلم على رفض الطلب";
  const modalSubtitle =
    appealModal.type === "penalty"
      ? "سيتم إرسال التظلم إلى المدير المباشر للنظر في إلغاء أو تعديل الجزاء"
      : "سيتم إرسال التظلم إلى المدير المباشر للمراجعة";

  return (
    <>
      <div className="ess-home-top">
        <div className="ess-home-header">
          <h1 className="ess-home-title">الرئيسية</h1>
          <p className="ess-home-subtitle">نظرة سريعة على حالتك الوظيفية</p>
        </div>
        <HomeAttendanceClock />
      </div>

      <HomeUpcomingShifts />

      <HomeQuickActions />

      <MyPenaltiesSections
        penalties={myPenalties}
        onAppeal={(penaltyId) => openAppealModal("penalty", penaltyId)}
      />

      <div className="ess-stats-grid">
        {stats.map((stat) => (
          <div key={stat.id} className="ess-stat-card">
            <span className="ess-stat-label">{stat.label}</span>
            <span className="ess-stat-value">{stat.value}</span>
            <span className="ess-stat-note">{stat.note}</span>
          </div>
        ))}
      </div>

      <HomeRecentRequests
        requests={requests}
        onAppeal={(requestId) => openAppealModal("request", requestId)}
        onResubmit={handleResubmit}
      />

      <HomeLeaveRequests requests={leaveRequests} />

      <AppealModal
        isOpen={appealModal.open}
        onClose={closeAppealModal}
        title={modalTitle}
        subtitle={modalSubtitle}
        onSubmit={handleAppealSubmit}
        submitted={appealModal.submitted}
      />
    </>
  );
}

// ✅ رئيسية المدير المباشر (manager) — بيانات مختلفة عن الموظف، تظلمه يروح لمدير الإدارة مباشرة
function ManagerOwnHomeContent() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const handleResubmit = useRequestResubmit();
  const {
    employeePenalties,
    refreshData,
    pendingRequests,
    getAllEmployees,
  } = useData();

  const myEmployee = useMemo(
    () => resolveMyEmployeeRecord(getAllEmployees(), user),
    [getAllEmployees, user],
  );

  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    if (myEmployee) {
      setMyProfile(myEmployee);
    }

    let cancelled = false;
    getMyEmployee()
      .then((data) => {
        if (!cancelled && data) {
          setMyProfile(mapEmployeeRow(data));
        } else if (!cancelled && !myEmployee) {
          setMyProfile(null);
        }
      })
      .catch(() => {
        if (!cancelled && !myEmployee) {
          setMyProfile(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [myEmployee, user?.employeeId]);

  const myProfileId = myProfile?.id || user?.employeeId;

  const myPenalties = useMemo(
    () => filterMyPenalties(employeePenalties, myProfileId),
    [employeePenalties, myProfileId],
  );

  const managerStats = useMemo(
    () =>
      buildManagerHomeStats({
        employee: myProfile,
        pendingRequests,
        managerPenalties: myPenalties,
        user,
      }),
    [myProfile, pendingRequests, myPenalties, user],
  );

  const requests = useMemo(
    () => buildHomeMyRequests(pendingRequests, user),
    [pendingRequests, user],
  );

  const leaveRequests = useMemo(
    () => buildUpcomingApprovedLeaves(pendingRequests, user),
    [pendingRequests, user],
  );

  const [appealModal, setAppealModal] = useState({
    open: false,
    type: "",
    itemId: null,
    submitted: false,
  });

  function openAppealModal(type, itemId) {
    setAppealModal({ open: true, type, itemId, submitted: false });
  }

  function closeAppealModal() {
    setAppealModal({ open: false, type: "", itemId: null, submitted: false });
  }

  async function handleAppealSubmit(reason) {
    try {
      await createAppeal({
        type: appealModal.type === "penalty" ? "penalty" : "request",
        reason,
        penaltyId:
          appealModal.type === "penalty" ? appealModal.itemId : undefined,
        relatedId:
          appealModal.type === "request" ? appealModal.itemId : undefined,
      });
      await refreshData();
      setAppealModal((prev) => ({ ...prev, submitted: true }));
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إرسال التظلم";
      showToast(Array.isArray(message) ? message.join("\n") : message, "error");
    }
  }

  const modalTitle =
    appealModal.type === "penalty"
      ? "تقديم تظلم على الجزاء"
      : "تقديم تظلم على رفض الطلب";
  const modalSubtitle =
    appealModal.type === "penalty"
      ? "سيتم إرسال التظلم إلى المستوى الأعلى للمراجعة"
      : "سيتم إرسال التظلم إلى المستوى الأعلى للمراجعة";

  return (
    <>
      <div className="ess-home-top">
        <div className="ess-home-header">
          <h1 className="ess-home-title">الرئيسية</h1>
          <p className="ess-home-subtitle">نظرة سريعة على حالتك كمدير</p>
        </div>
        <HomeAttendanceClock />
      </div>

      <HomeUpcomingShifts />

      <ManagerTeamAttendance />

      <HomeQuickActions />

      <MyPenaltiesSections
        penalties={myPenalties}
        onAppeal={(penaltyId) => openAppealModal("penalty", penaltyId)}
      />

      <div className="ess-stats-grid">
        {managerStats.map((stat) => (
          <div key={stat.id} className="ess-stat-card">
            <span className="ess-stat-label">{stat.label}</span>
            <span className="ess-stat-value">{stat.value}</span>
            <span className="ess-stat-note">{stat.note}</span>
          </div>
        ))}
      </div>

      <HomeRecentRequests
        requests={requests}
        onAppeal={(requestId) => openAppealModal("request", requestId)}
        onResubmit={handleResubmit}
      />

      <HomeLeaveRequests requests={leaveRequests} />

      <AppealModal
        isOpen={appealModal.open}
        onClose={closeAppealModal}
        title={modalTitle}
        subtitle={modalSubtitle}
        onSubmit={handleAppealSubmit}
        submitted={appealModal.submitted}
      />
    </>
  );
}

// ✅ رئيسية مدير الإدارة (department-manager)
function ManagerHomeContent() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const {
    employeePenalties,
    refreshData,
    pendingRequests,
    getAllEmployees,
  } = useData();

  const myEmployee = useMemo(
    () => resolveMyEmployeeRecord(getAllEmployees(), user),
    [getAllEmployees, user],
  );

  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    if (myEmployee) {
      setMyProfile(myEmployee);
    }

    let cancelled = false;
    getMyEmployee()
      .then((data) => {
        if (!cancelled && data) {
          setMyProfile(mapEmployeeRow(data));
        } else if (!cancelled && !myEmployee) {
          setMyProfile(null);
        }
      })
      .catch(() => {
        if (!cancelled && !myEmployee) {
          setMyProfile(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [myEmployee, user?.employeeId]);

  const myProfileId = myProfile?.id || user?.employeeId;

  const myPenalties = useMemo(
    () => filterMyPenalties(employeePenalties, myProfileId),
    [employeePenalties, myProfileId],
  );

  const managerStats = useMemo(
    () =>
      buildDepartmentManagerHomeStats({
        employee: myProfile,
        pendingRequests,
        managerPenalties: myPenalties,
        user,
      }),
    [myProfile, pendingRequests, myPenalties, user],
  );

  const [appealModal, setAppealModal] = useState({
    open: false,
    type: "",
    itemId: null,
    submitted: false,
  });

  function openAppealModal(itemId) {
    setAppealModal({
      open: true,
      type: "penalty",
      itemId,
      submitted: false,
    });
  }

  function closeAppealModal() {
    setAppealModal({ open: false, type: "", itemId: null, submitted: false });
  }

  async function handleAppealSubmit(reason) {
    try {
      await createAppeal({
        type: "penalty",
        reason,
        penaltyId: appealModal.itemId,
      });
      await refreshData();
      setAppealModal((prev) => ({ ...prev, submitted: true }));
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إرسال التظلم";
      showToast(Array.isArray(message) ? message.join("\n") : message, "error");
    }
  }

  return (
    <>
      <div className="ess-home-top">
        <div className="ess-home-header">
          <h1 className="ess-home-title">الرئيسية</h1>
          <p className="ess-home-subtitle">نظرة سريعة على حالتك الوظيفية</p>
        </div>
        <HomeAttendanceClock />
      </div>

      <HomeUpcomingShifts />

      <ManagerTeamAttendance />

      <HomeQuickActions />

      <MyPenaltiesSections
        penalties={myPenalties}
        onAppeal={(penaltyId) => openAppealModal(penaltyId)}
      />

      <div className="ess-stats-grid">
        {managerStats.map((stat) => (
          <div key={stat.id} className="ess-stat-card">
            <span className="ess-stat-label">{stat.label}</span>
            <span className="ess-stat-value">{stat.value}</span>
            <span className="ess-stat-note">{stat.note}</span>
          </div>
        ))}
      </div>

      <AppealModal
        isOpen={appealModal.open}
        onClose={closeAppealModal}
        title="تقديم تظلم على الجزاء"
        subtitle="سيتم إرسال التظلم إلى المستوى الأعلى للمراجعة"
        onSubmit={handleAppealSubmit}
        submitted={appealModal.submitted}
      />
    </>
  );
}

function LeaveContent() {
  return <MyRequestsHub />;
}

function AdvanceContent() {
  const { user } = useAuth();
  return (
    <>
      <div className="ess-home-header">
        <h1 className="ess-home-title">طلبات السلف</h1>
        <p className="ess-home-subtitle">
          {getRequestSubmitHint(user?.role, "advance")}
        </p>
      </div>
      <AdvanceRequestForm />
    </>
  );
}

function ComplaintsContent() {
  return <MyComplaints />;
}

function ReceivedComplaintsContent({ userRole }) {
  return <ReceivedComplaints userRole={userRole} />;
}

function PlaceholderContent({ title, subtitle }) {
  return (
    <>
      <div className="ess-home-header">
        <h1 className="ess-home-title">{title}</h1>
        <p className="ess-home-subtitle">{subtitle}</p>
      </div>
      <div className="ess-placeholder-box">محتوى الصفحة قيد الإعداد</div>
    </>
  );
}

function Main({ activeId, className = "", userRole, userName, focusAnnouncementId = null, focusPolicyId = null }) {
  if (activeId === "my-profile") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        <MyProfile />
      </div>
    );
  }

  if (activeId === "my-payslip") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        <FeatureGate feature="payroll">
          <MyPayslip canManageLogo={userRole === "hr"} />
        </FeatureGate>
      </div>
    );
  }

  if (
    activeId === "profile-completion" &&
    (userRole === "admin" ||
      userRole === "gm" ||
      isOwnerRole(userRole) ||
      isHrFamilyRole(userRole))
  ) {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        <HrProfileCompletion userRole={userRole} />
      </div>
    );
  }

  if (userRole === "platform-admin") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        {activeId === "company-modules" && <CompanyModulesPanel />}
      </div>
    );
  }

  // Manager (branch manager / regional manager / section manager)
  if (
    activeId === "social-insurance" &&
    (userRole === "admin" ||
      userRole === "gm" ||
      isOwnerRole(userRole) ||
      isHrFamilyRole(userRole))
  ) {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        <HrSocialInsurance userRole={userRole} />
      </div>
    );
  }

  if (userRole === "manager") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        {activeId === "home" && <ManagerOwnHomeContent />}
        {activeId === "notifications" && <RequestsNotifications />}
        {activeId === "announcements" && (
          <AnnouncementsInbox initialAnnouncementId={focusAnnouncementId} />
        )}
        {activeId === "company-policies" && (
          <CompanyPoliciesInbox initialPolicyId={focusPolicyId} />
        )}
        {activeId === "my-days" && (
          <FeatureGate feature="attendance">
            <MyDaysView userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "team" && <TeamManagement userRole={userRole} />}
        {activeId === "agenda" && <EmployeeAgenda userRole={userRole} />}
        {activeId === "team-leave-overtime" && <TeamLeaveOvertimeView />}
        {activeId === "approvals" && <PendingApprovals teamOnly />}
        {activeId === "attendance" && (
          <FeatureGate feature="attendance"><RemoteAttendance /></FeatureGate>
        )}
        {activeId === "overtime" && (
          <FeatureGate feature="overtime"><OvertimeRequestForm /></FeatureGate>
        )}
        {activeId === "wfh" && <WfhRequestForm />}
        {activeId === "branch-timesheet" && (
          <FeatureGate feature="attendance"><BranchTimesheet /></FeatureGate>
        )}
        {activeId === "penalties" && <PenaltyManager userRole={userRole} />}
        {activeId === "appeals" && <AppealsManager userRole={userRole} />}
        {activeId === "investigations" && (
          <FeatureGate feature="complaints">
            <InvestigationsPage />
          </FeatureGate>
        )}
        {activeId === "received-complaints" && (
          <FeatureGate feature="complaints">
            <ReceivedComplaintsContent userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "my-complaints" && (
          <FeatureGate feature="complaints"><ComplaintsContent /></FeatureGate>
        )}
        {activeId === "resignations" && <ResignationReview />}
        {activeId === "leave" && (
          <FeatureGate feature="leave"><LeaveContent /></FeatureGate>
        )}
        {activeId === "advance" && <AdvanceContent />}
        {activeId === "advance-disbursement" && <AdvanceDisbursementRouting />}
        {activeId === "advance-confirm" && <AdvanceDisbursementConfirm />}
        {activeId === "advance-ledger" && <AdvanceDisbursementLedger />}
        {activeId === "cash-entitlement-disbursement" && <CashEntitlementRouting />}
        {activeId === "cash-entitlement-confirm" && <CashEntitlementConfirm />}
        {activeId === "cash-entitlement-ledger" && <CashEntitlementLedger />}
        {activeId === "evaluations" && (
          <FeatureGate feature="evaluations"><MyEvaluations /></FeatureGate>
        )}
        {activeId === "recruitment" && (
          <FeatureGate feature="recruitment"><ManagerRecruitment /></FeatureGate>
        )}
      </div>
    );
  }

  // Department Manager
  if (userRole === "department-manager") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        {activeId === "dashboard" && <ManagerHomeContent />}
        {activeId === "notifications" && <RequestsNotifications />}
        {activeId === "announcements" && (
          <AnnouncementsInbox initialAnnouncementId={focusAnnouncementId} />
        )}
        {activeId === "company-policies" && (
          <CompanyPoliciesInbox initialPolicyId={focusPolicyId} />
        )}
        {activeId === "my-days" && (
          <FeatureGate feature="attendance">
            <MyDaysView userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "departments" && <BranchesDepartments userRole={userRole} />}
        {activeId === "agenda" && <EmployeeAgenda userRole={userRole} />}
        {activeId === "team-leave-overtime" && <TeamLeaveOvertimeView />}
        {activeId === "approvals" && <PendingApprovals />}
        {activeId === "attendance" && (
          <FeatureGate feature="attendance"><RemoteAttendance /></FeatureGate>
        )}
        {activeId === "overtime" && (
          <FeatureGate feature="overtime"><OvertimeRequestForm /></FeatureGate>
        )}
        {activeId === "wfh" && <WfhRequestForm />}
        {activeId === "branch-timesheet" && (
          <FeatureGate feature="attendance"><BranchTimesheet /></FeatureGate>
        )}
        {activeId === "penalties" && <PenaltyManager userRole={userRole} />}
        {activeId === "appeals" && <AppealsManager userRole={userRole} />}
        {activeId === "investigations" && (
          <FeatureGate feature="complaints">
            <InvestigationsPage />
          </FeatureGate>
        )}
        {activeId === "received-complaints" && (
          <FeatureGate feature="complaints">
            <ReceivedComplaintsContent userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "my-complaints" && (
          <FeatureGate feature="complaints"><ComplaintsContent /></FeatureGate>
        )}
        {activeId === "resignations" && <ResignationReview />}
        {activeId === "leave" && (
          <FeatureGate feature="leave"><LeaveContent /></FeatureGate>
        )}
        {activeId === "advance" && <AdvanceContent />}
        {activeId === "advance-disbursement" && <AdvanceDisbursementRouting />}
        {activeId === "advance-confirm" && <AdvanceDisbursementConfirm />}
        {activeId === "advance-ledger" && <AdvanceDisbursementLedger />}
        {activeId === "cash-entitlement-disbursement" && <CashEntitlementRouting />}
        {activeId === "cash-entitlement-confirm" && <CashEntitlementConfirm />}
        {activeId === "cash-entitlement-ledger" && <CashEntitlementLedger />}
        {activeId === "evaluations" && (
          <FeatureGate feature="evaluations"><MyEvaluations /></FeatureGate>
        )}
        {activeId === "recruitment" && (
          <FeatureGate feature="recruitment"><ManagerRecruitment /></FeatureGate>
        )}
      </div>
    );
  }

  // Admin — أدمن النظام: حسابات القيادة + أي موظف/دور آخر
  if (userRole === "admin") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        {activeId === "accounts" && (
          <>
            <div className="ess-home-top">
              <HomeAttendanceClock />
            </div>
            <AdminAccountsManagement />
          </>
        )}
        {activeId === "employees" && (
          <HrEmployeeManagement userRole={userRole} />
        )}
        {activeId === "agenda" && <EmployeeAgenda userRole={userRole} />}
        {activeId === "work-mode" && (
          <FeatureGate feature="attendance"><HrWorkModeManagement /></FeatureGate>
        )}
        {activeId === "departments" && (
          <BranchesDepartments userRole={userRole} />
        )}
        {activeId === "approval-cycles" && canManageApprovalCycles(userRole) && (
          <HrWorkflows />
        )}
        {activeId === "announcements-manage" && <HrAnnouncements />}
        {activeId === "company-policies-manage" && <HrCompanyPolicies />}
        {activeId === "leave-grant" && (
          <FeatureGate feature="leave"><HrLeaveGrant /></FeatureGate>
        )}
        {activeId === "evaluations-manage" && (
          <FeatureGate feature="evaluations"><HrEvaluations /></FeatureGate>
        )}
        {activeId === "evaluations" && (
          <FeatureGate feature="evaluations"><MyEvaluations /></FeatureGate>
        )}
        {activeId === "recruitment" && (
          <FeatureGate feature="recruitment"><HrRecruitment mode="admin" /></FeatureGate>
        )}
        {activeId === "investigations" && (
          <FeatureGate feature="complaints">
            <InvestigationsPage />
          </FeatureGate>
        )}
        {activeId === "brands-admin" && isBrandAdminUiEnabled() && (
          <BrandsManagement />
        )}
        {activeId === "advance-disbursement" && <AdvanceDisbursementRouting />}
        {activeId === "advance-ledger" && <AdvanceDisbursementLedger />}
        {activeId === "cash-entitlement-create" && <HrCashEntitlementCreate />}
        {activeId === "cash-entitlement-disbursement" && <CashEntitlementRouting />}
        {activeId === "cash-entitlement-ledger" && <CashEntitlementLedger />}
      </div>
    );
  }

  // GM — المدير العام (فوق HR)
  if (userRole === "gm") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        {activeId === "dashboard" && <GmDashboard />}
        {activeId === "notifications" && <RequestsNotifications />}
        {activeId === "announcements" && (
          <AnnouncementsInbox initialAnnouncementId={focusAnnouncementId} />
        )}
        {activeId === "company-policies" && (
          <CompanyPoliciesInbox initialPolicyId={focusPolicyId} />
        )}
        {activeId === "announcements-manage" && <HrAnnouncements />}
        {activeId === "company-policies-manage" && <HrCompanyPolicies />}
        {activeId === "leave-grant" && (
          <FeatureGate feature="leave"><HrLeaveGrant /></FeatureGate>
        )}
        {activeId === "approvals" && (
          <>
            <PendingApprovals sources={["leave", "permit", "overtime"]} />
            <HrAdvanceApprovals />
          </>
        )}
        {activeId === "departments" && <BranchesDepartments userRole={userRole} />}
        {activeId === "approval-cycles" && canManageApprovalCycles(userRole) && (
          <HrWorkflows />
        )}
        {activeId === "employees" && <HrEmployeeManagement userRole={userRole} />}
        {activeId === "agenda" && <EmployeeAgenda userRole={userRole} />}
        {activeId === "work-mode" && (
          <FeatureGate feature="attendance"><HrWorkModeManagement /></FeatureGate>
        )}
        {activeId === "flexible-manual-attendance" && (
          <FeatureGate feature="attendance"><FlexibleManualAttendance /></FeatureGate>
        )}
        {activeId === "requests" && <GmDashboard defaultTab="requests" />}
        {activeId === "kmt-upload" && (
          <FeatureGate feature="attendance"><KmtUpload /></FeatureGate>
        )}
        {activeId === "attendance-summary" && (
          <FeatureGate feature="attendance">
            <ReportsHub initialTab="attendance-summary" />
          </FeatureGate>
        )}
        {activeId === "branch-timesheet" && (
          <FeatureGate feature="attendance"><BranchTimesheet /></FeatureGate>
        )}
        {activeId === "branch-shift-hours" && (
          <FeatureGate feature="attendance"><BranchShiftHours /></FeatureGate>
        )}
        {activeId === "custom-schedule" && (
          <FeatureGate feature="attendance"><CustomSchedule /></FeatureGate>
        )}
        {activeId === "transport-lines" && (
          <FeatureGate feature="attendance"><TransportLines /></FeatureGate>
        )}
        {activeId === "attendance-regulations" && (
          <FeatureGate feature="attendance">
            <AttendanceRegulations userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "kmt-payroll" && (
          <FeatureGate feature="payroll"><KmtPayroll /></FeatureGate>
        )}
        {activeId === "sales-targets" && (
          <FeatureGate feature="payroll"><SalesTargets /></FeatureGate>
        )}
        {activeId === "penalties" && <PenaltyManager userRole={userRole} />}
        {activeId === "shortage" && <HrShortageManager userRole={userRole} />}
        {activeId === "appeals" && <AppealsManager userRole={userRole} />}
        {activeId === "investigations" && (
          <FeatureGate feature="complaints">
            <InvestigationsPage />
          </FeatureGate>
        )}
        {activeId === "received-complaints" && (
          <FeatureGate feature="complaints">
            <ReceivedComplaintsContent userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "resignations" && <ResignationReview />}
        {activeId === "reports" && <ReportsHub />}
        {activeId === "advance-disbursement" && <AdvanceDisbursementRouting />}
        {activeId === "advance-ledger" && <AdvanceDisbursementLedger />}
        {activeId === "cash-entitlement-create" && <HrCashEntitlementCreate />}
        {activeId === "cash-entitlement-disbursement" && <CashEntitlementRouting />}
        {activeId === "cash-entitlement-ledger" && <CashEntitlementLedger />}
        {activeId === "evaluations-results" && (
          <FeatureGate feature="evaluations">
            <EvaluationResultsBoard />
          </FeatureGate>
        )}
        {activeId === "evaluations-manage" && (
          <FeatureGate feature="evaluations"><HrEvaluations /></FeatureGate>
        )}
        {activeId === "evaluations" && (
          <FeatureGate feature="evaluations"><MyEvaluations /></FeatureGate>
        )}
        {activeId === "recruitment" && (
          <FeatureGate feature="recruitment"><HrRecruitment mode="gm" /></FeatureGate>
        )}
      </div>
    );
  }

  // Owner — مشاهدة كل الشركة بدون موافقة أو رفض أو تعديل
  if (isOwnerRole(userRole)) {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        {activeId === "dashboard" && <GmDashboard />}
        {activeId === "notifications" && <RequestsNotifications />}
        {activeId === "announcements" && (
          <AnnouncementsInbox initialAnnouncementId={focusAnnouncementId} />
        )}
        {activeId === "company-policies" && (
          <CompanyPoliciesInbox initialPolicyId={focusPolicyId} />
        )}
        {activeId === "departments" && <BranchesDepartments userRole={userRole} />}
        {activeId === "employees" && <HrEmployeeManagement userRole={userRole} />}
        {activeId === "agenda" && (
          <EmployeeAgenda userRole={userRole} readOnly />
        )}
        {activeId === "requests" && <GmDashboard defaultTab="requests" />}
        {activeId === "attendance-summary" && (
          <FeatureGate feature="attendance">
            <ReportsHub initialTab="attendance-summary" />
          </FeatureGate>
        )}
        {activeId === "branch-timesheet" && (
          <FeatureGate feature="attendance"><BranchTimesheet /></FeatureGate>
        )}
        {activeId === "inventory-night" && (
          <FeatureGate feature="attendance"><InventoryNightPage /></FeatureGate>
        )}
        {activeId === "kmt-payroll" && (
          <FeatureGate feature="payroll"><KmtPayroll /></FeatureGate>
        )}
        {activeId === "sales-targets" && (
          <FeatureGate feature="payroll"><SalesTargets /></FeatureGate>
        )}
        {activeId === "attendance-regulations" && (
          <FeatureGate feature="attendance">
            <AttendanceRegulations userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "transport-lines" && (
          <FeatureGate feature="attendance">
            <TransportLines readOnly />
          </FeatureGate>
        )}
        {activeId === "penalties" && <PenaltyManager userRole={userRole} />}
        {activeId === "shortage" && <HrShortageManager userRole={userRole} />}
        {activeId === "appeals" && <AppealsManager userRole={userRole} />}
        {activeId === "investigations" && (
          <FeatureGate feature="complaints">
            <InvestigationsPage />
          </FeatureGate>
        )}
        {activeId === "received-complaints" && (
          <FeatureGate feature="complaints">
            <ReceivedComplaintsContent userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "resignations" && <ResignationReview />}
        {activeId === "reports" && <ReportsHub />}
        {activeId === "recruitment" && (
          <FeatureGate feature="recruitment"><HrRecruitment mode="gm" /></FeatureGate>
        )}
      </div>
    );
  }

  // HR / HR staff (same screens; ZKT payroll gated in sidebar)
  if (userRole === "hr" || userRole === "hr-staff") {
    return (
      <div className={`ess-home ${className}`} dir="rtl">
        {activeId === "dashboard" && <HrDashboard />}
        {activeId === "notifications" && <RequestsNotifications />}
        {activeId === "announcements" && (
          <AnnouncementsInbox initialAnnouncementId={focusAnnouncementId} />
        )}
        {activeId === "company-policies" && (
          <CompanyPoliciesInbox initialPolicyId={focusPolicyId} />
        )}
        {activeId === "announcements-manage" && <HrAnnouncements />}
        {activeId === "company-policies-manage" && <HrCompanyPolicies />}
        {activeId === "my-days" && (
          <FeatureGate feature="attendance">
            <MyDaysView userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "leave-grant" && (
          <FeatureGate feature="leave"><HrLeaveGrant /></FeatureGate>
        )}
        {activeId === "leave" && (
          <FeatureGate feature="leave"><LeaveContent /></FeatureGate>
        )}
        {activeId === "advance" && <AdvanceContent />}
        {activeId === "attendance" && (
          <FeatureGate feature="attendance"><RemoteAttendance /></FeatureGate>
        )}
        {activeId === "overtime" && (
          <FeatureGate feature="overtime"><OvertimeRequestForm /></FeatureGate>
        )}
        {activeId === "overtime-grant" && (
          <FeatureGate feature="overtime"><HrOvertimeGrant /></FeatureGate>
        )}
        {activeId === "wfh" && <WfhRequestForm />}
        {activeId === "advance-approvals" && <HrAdvanceApprovals />}
        {activeId === "cash-entitlement-create" && <HrCashEntitlementCreate />}
        {activeId === "advance-disbursement" && <AdvanceDisbursementRouting />}
        {activeId === "advance-confirm" && <AdvanceDisbursementConfirm />}
        {activeId === "advance-ledger" && <AdvanceDisbursementLedger />}
        {activeId === "cash-entitlement-disbursement" && <CashEntitlementRouting />}
        {activeId === "cash-entitlement-confirm" && <CashEntitlementConfirm />}
        {activeId === "cash-entitlement-ledger" && <CashEntitlementLedger />}
        {activeId === "team-approvals" && (
          <PendingApprovals sources={["leave", "weekly-rest", "permit", "overtime"]} />
        )}
        {activeId === "employees" && <HrEmployeeManagement userRole={userRole} />}
        {activeId === "agenda" && <EmployeeAgenda userRole={userRole} />}
        {activeId === "work-mode" && (
          <FeatureGate feature="attendance"><HrWorkModeManagement /></FeatureGate>
        )}
        {activeId === "flexible-manual-attendance" && (
          <FeatureGate feature="attendance"><FlexibleManualAttendance /></FeatureGate>
        )}
        {activeId === "departments" && <HrOrgStructure />}
        {activeId === "approval-cycles" && canManageApprovalCycles(userRole) && (
          <HrWorkflows />
        )}
        {activeId === "kmt-upload" && (
          <FeatureGate feature="attendance"><KmtUpload /></FeatureGate>
        )}
        {activeId === "attendance-summary" && (
          <FeatureGate feature="attendance">
            <ReportsHub initialTab="attendance-summary" />
          </FeatureGate>
        )}
        {activeId === "branch-timesheet" && (
          <FeatureGate feature="attendance"><BranchTimesheet /></FeatureGate>
        )}
        {activeId === "inventory-night" && (
          <FeatureGate feature="attendance"><InventoryNightPage /></FeatureGate>
        )}
        {activeId === "branch-shift-hours" && (
          <FeatureGate feature="attendance"><BranchShiftHours /></FeatureGate>
        )}
        {activeId === "custom-schedule" && (
          <FeatureGate feature="attendance"><CustomSchedule /></FeatureGate>
        )}
        {activeId === "transport-lines" && (
          <FeatureGate feature="attendance"><TransportLines /></FeatureGate>
        )}
        {activeId === "attendance-regulations" && (
          <FeatureGate feature="attendance">
            <AttendanceRegulations userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "kmt-payroll" && userRole === "hr" && (
          <FeatureGate feature="payroll"><KmtPayroll /></FeatureGate>
        )}
        {activeId === "sales-targets" && userRole === "hr" && (
          <FeatureGate feature="payroll"><SalesTargets /></FeatureGate>
        )}
        {activeId === "penalties" && <PenaltyManager userRole={userRole} />}
        {activeId === "shortage" && <HrShortageManager userRole={userRole} />}
        {activeId === "appeals" && <AppealsManager userRole={userRole} />}
        {activeId === "investigations" && (
          <FeatureGate feature="complaints">
            <InvestigationsPage />
          </FeatureGate>
        )}
        {activeId === "received-complaints" && (
          <FeatureGate feature="complaints">
            <ReceivedComplaintsContent userRole={userRole} />
          </FeatureGate>
        )}
        {activeId === "resignations" && <ResignationReview />}
        {activeId === "reports" && <ReportsHub />}
        {activeId === "evaluations-results" && (
          <FeatureGate feature="evaluations">
            <EvaluationResultsBoard />
          </FeatureGate>
        )}
        {activeId === "evaluations-manage" && (
          <FeatureGate feature="evaluations"><HrEvaluations /></FeatureGate>
        )}
        {activeId === "evaluations" && (
          <FeatureGate feature="evaluations"><MyEvaluations /></FeatureGate>
        )}
        {activeId === "recruitment" && (
          <FeatureGate feature="recruitment"><HrRecruitment mode="hr" /></FeatureGate>
        )}
      </div>
    );
  }

  // Employee (default)
  return (
    <div className={`ess-home ${className}`} dir="rtl">
      {activeId === "home" ? (
        <HomeContent />
      ) : activeId === "notifications" ? (
        <RequestsNotifications />
      ) : activeId === "announcements" ? (
        <AnnouncementsInbox initialAnnouncementId={focusAnnouncementId} />
      ) : activeId === "company-policies" ? (
        <CompanyPoliciesInbox initialPolicyId={focusPolicyId} />
      ) : activeId === "my-days" ? (
        <FeatureGate feature="attendance"><MyDaysView userRole={userRole} /></FeatureGate>
      ) : activeId === "attendance" ? (
        <FeatureGate feature="attendance"><RemoteAttendance /></FeatureGate>
      ) : activeId === "overtime" ? (
        <FeatureGate feature="overtime"><OvertimeRequestForm /></FeatureGate>
      ) : activeId === "wfh" ? (
        <WfhRequestForm />
      ) : activeId === "leave" ? (
        <FeatureGate feature="leave"><LeaveContent /></FeatureGate>
      ) : activeId === "advance" ? (
        <AdvanceContent />
      ) : activeId === "advance-disbursement" ? (
        <AdvanceDisbursementRouting />
      ) : activeId === "advance-confirm" ? (
        <AdvanceDisbursementConfirm />
      ) : activeId === "advance-ledger" ? (
        <AdvanceDisbursementLedger />
      ) : activeId === "cash-entitlement-disbursement" ? (
        <CashEntitlementRouting />
      ) : activeId === "cash-entitlement-confirm" ? (
        <CashEntitlementConfirm />
      ) : activeId === "cash-entitlement-ledger" ? (
        <CashEntitlementLedger />
      ) : activeId === "my-complaints" ? (
        <FeatureGate feature="complaints"><ComplaintsContent /></FeatureGate>
      ) : activeId === "appeals" ? (
        <MyAppeals />
      ) : activeId === "investigations" ? (
        <FeatureGate feature="complaints"><InvestigationsPage /></FeatureGate>
      ) : activeId === "evaluations" ? (
        <FeatureGate feature="evaluations"><MyEvaluations /></FeatureGate>
      ) : activeId === "agenda" ? (
        <EmployeeAgenda userRole={userRole} />
      ) : activeId === "internal-jobs" ? (
        <FeatureGate feature="recruitment"><InternalJobs /></FeatureGate>
      ) : activeId === "branch-timesheet" ? (
        <FeatureGate feature="attendance"><BranchTimesheet /></FeatureGate>
      ) : (
        <PlaceholderContent
          title={pageInfo[activeId]?.title}
          subtitle={pageInfo[activeId]?.subtitle}
        />
      )}
    </div>
  );
}

export default Main;
